import { NextPage } from 'next'
import { useMemo } from 'react'

import { useUser } from 'src/providers/UserProvider'

import { SetupAccount } from 'src/components/atoms/Icons'
import { ButtonLink } from 'src/components/atoms/Link'
import { LoadingSpinner } from 'src/components/atoms/LoadingSpinner'
import { Page } from 'src/components/layouts/Page'
import { DashboardHeader } from 'src/components/molecules/DashboardHeader'
import { ZeroState } from 'src/components/molecules/ZeroState'

import { accountSetupPath } from 'src/config/paths'
import { ActiveProfileType } from 'src/types/account'
import { withAuthRequired } from 'src/utils/auth'

import { ImpactDashboardPanel } from '../../components/organisms/Dashboard/boards/impact'
import { PlanterDashboardPanel } from '../../components/organisms/Dashboard/boards/planter'

const DashboardZeroState = () => {
  return (
    <div className='bg-white rounded-[10px] p-5 md:px-[100px] sm:py-10 flex flex-col items-center'>
      <ZeroState
        image={<SetupAccount className='w-[120px] h-[120px]' />}
        title='You’re one step away from getting started!'
        description='Set up your account to start tracking your impact or request trees.'
        cta={
          <ButtonLink
            className='self-center'
            variant='contained'
            color='primary'
            href={accountSetupPath}
            size='large'
          >
            Set up account
          </ButtonLink>
        }
      />
    </div>
  )
}

type DashboardProps = {
  selectedDashboard: ActiveProfileType
}

const Dashboard = ({ selectedDashboard }: DashboardProps) => {
  const dashboardPanel = useMemo(() => {
    if (selectedDashboard === ActiveProfileType.Funder) {
      return <ImpactDashboardPanel />
    }
    return <PlanterDashboardPanel />
  }, [selectedDashboard])

  return dashboardPanel
}

const DashboardPage: NextPage = () => {
  const { user, isLoading } = useUser()

  const content = useMemo(() => {
    if (isLoading) {
      return <LoadingSpinner isLoading />
    }
    if (!user?.Account) {
      return <DashboardZeroState />
    }

    return (
      <Dashboard
        selectedDashboard={user.ActiveProfileType as ActiveProfileType}
      />
    )
  }, [user, isLoading])

  return (
    <Page isFullWidth isIntercomEnabled>
      <div className='2xl:max-w-[1220px] flex flex-col gap-5 sm:gap-10 mx-4 md:mx-[60px] lg:mx-11 xl:mx-[110px] 2xl:mx-auto py-[30px] md:py-12 xl:py-[60px]'>
        <DashboardHeader user={user} />
        {content}
      </div>
    </Page>
  )
}

export default withAuthRequired(DashboardPage)
